/**
 * @file React hook — useContributorDetails.ts: form/state logic for contributor dashboard.
 */

import { useEffect, useRef, useState } from "react";
import { FORM_STAGE_CODES } from "../../../../../../constants/formStages";
import {
  FormDataResponse,
  FormField,
  FormSubSection,
} from "../../../../../../interfaces/formInterfaces";
import {
  getProfile,
  getContributorFormData,
  getSubmission,
  saveContributorDetails,
} from "../../../../../../services/contributorService";
import { toast } from "react-toastify";
import {
  getFirstFormError,
  validateContributorDetailsForm,
} from "../utils/validateContributorDetailsForm";
import { clearFieldErrorsFromMap } from "../../shared/contributorFormValidation";

/** Hook: use Contributor Details. */
export const useContributorDetails = (submissionId?: string) => {
  const [formData, setFormData] = useState<FormDataResponse | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const lastSavedRef = useRef<Record<string, string>>({});
  const [hasSavedOnce, setHasSavedOnce] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const shallowEqual = (a: Record<string, string>, b: Record<string, string>) => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
      if (a[k] !== b[k]) return false;
    }
    return true;
  };

  /* ============================================================
     LOAD PAGE
  ============================================================ */
  useEffect(() => {
    const loadPage = async () => {
      try {
        setLoading(true);

        /* ---------- PROFILE ---------- */
        const profileResult = await getProfile();
        if (!profileResult.success) {
          toast.error(profileResult.error || "Failed to load profile");
          return;
        }

        /* ---------- FORM ---------- */
        const formResult = await getContributorFormData(
          FORM_STAGE_CODES.CONTRIBUTOR_DETAILS
        );
        if (!formResult.success || !formResult.data) {
          toast.error(formResult.error || "Failed to load form data");
          return;
        }

        const form: FormDataResponse = formResult.data;
        setFormData(form);

        /* ---------- ALL FIELDS ---------- */
        const allFields: FormField[] = [
          ...(form.fields || []),
          ...(form.ungroupedFields || []),
          ...(form.subSections?.flatMap((s: FormSubSection) => s.fields) || []),
        ];

        const dataSourceField = allFields.find(
          (f) => f.name === "data_source" || f.name === "dataSource"
        );
        const dataSourceKey = dataSourceField?.name ?? "data_source";

        /* ---------- INIT VALUES ---------- */
        const initialValues: Record<string, string> = {};
        allFields.forEach((f) => (initialValues[f.name] = ""));

        /* ---------- PREFILL PROFILE ---------- */
        if (profileResult.data) {
          initialValues["name"] = profileResult.data.name || "";
          initialValues["email_address"] = profileResult.data.email || "";
          initialValues["Contributor_ID"] = profileResult.data.userCode || "";
        }

        /* ---------- FETCH SUBMISSION ---------- */
        if (submissionId) {
          const submissionResult = await getSubmission(submissionId);

          if (submissionResult.success && submissionResult.data) {
            const contributor = submissionResult.data.contributorProfile;

            if (contributor) {
              initialValues["affiliation"] = contributor.affiliation || "";
              initialValues["background"] = contributor.background || "";
              initialValues["background_other"] =
                contributor.backgroundOther || "";

              // Match saved contributor.dataSource to dropdown option value (field may be data_source or dataSource)
              let dataSourceValue = contributor.dataSource || "";

              if (dataSourceField && dataSourceField.type === "DROPDOWN") {
                const dropdownOptions =
                  dataSourceField.dropdown?.options || [];
                const apiValue = String(contributor.dataSource || "")
                  .toLowerCase()
                  .trim();

                const matchingOption = dropdownOptions.find((opt: any) => {
                  const optValue = String(opt.value || "").toLowerCase().trim();
                  const optLabel = String(opt.label || "").toLowerCase().trim();
                  return (
                    optValue === apiValue ||
                    optLabel === apiValue ||
                    String(opt.value) === String(contributor.dataSource)
                  );
                });

                if (matchingOption) {
                  dataSourceValue = String(matchingOption.value);
                }
              }

              initialValues[dataSourceKey] = dataSourceValue;
            }

            // If we already have contributor profile data, treat it as already saved.
            setHasSavedOnce(Boolean(contributor));
          } else {
            toast.error(
              submissionResult.error || "Failed to load saved submission"
            );
          }
        }

        // Default Data Source to "Person" when empty and the dropdown includes that option
        if (dataSourceField?.type === "DROPDOWN") {
          const opts = dataSourceField.dropdown?.options || [];
          const personOption = opts.find(
            (opt: { value?: string }) =>
              String(opt.value || "").toLowerCase().trim() === "person"
          );
          if (personOption && !(initialValues[dataSourceKey] || "").trim()) {
            initialValues[dataSourceKey] = String(personOption.value);
          }
        }

        setValues(initialValues);
        lastSavedRef.current = initialValues;
        setIsDirty(false);
      } catch (err: any) {
        console.error("ContributorDetails load error:", err);
        toast.error(
          err?.message || "An unexpected error occurred while loading the form"
        );
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [submissionId]);

  // Track dirty state based on last saved snapshot.
  useEffect(() => {
    const saved = lastSavedRef.current || {};
    setIsDirty(!shallowEqual(values, saved));
  }, [values]);

  /* ============================================================
     CHANGE HANDLER
  ============================================================ */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setValues((prev) => {
      const newValues = { ...prev, [name]: value };
      
      // If background changes and it's not "Other", clear background_other
      if (name === "background") {
        const valueLower = value.toLowerCase().trim();
        let isOther = valueLower === "other" || valueLower.includes("other");
        
        // Check dropdown options if formData is available
        if (!isOther && formData) {
          const allFields: FormField[] = [
            ...(formData.fields || []),
            ...(formData.ungroupedFields || []),
            ...(formData.subSections?.flatMap((s: FormSubSection) => s.fields) || []),
          ];
          
          const backgroundField = allFields.find((f) => f.name === "background");
          if (backgroundField && backgroundField.type === "DROPDOWN") {
            const options = backgroundField.dropdown?.options || [];
            const selectedOption = options.find(
              (opt: any) => String(opt.value).toLowerCase() === valueLower ||
              String(opt.label).toLowerCase() === valueLower
            );
            
            if (selectedOption) {
              const labelLower = String(selectedOption.label || "").toLowerCase();
              isOther = labelLower.includes("other");
            }
          }
        }
        
        if (!isOther) {
          newValues["background_other"] = "";
        }
      }
      
      return newValues;
    });

    if (errors[name] || (name === "background" && errors.background_other)) {
      setErrors((prev) =>
        clearFieldErrorsFromMap(
          prev,
          name,
          ...(name === "background" ? ["background_other"] : [])
        )
      );
    }
  };

  /* ============================================================
     VALIDATION
  ============================================================ */
  const runValidation = (): boolean => {
    if (!formData) return false;
    const formErrors = validateContributorDetailsForm(formData, values);
    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) {
      toast.error(
        getFirstFormError(formErrors) ?? "Please fix the highlighted fields."
      );
      return false;
    }
    return true;
  };

  /* ============================================================
     SUBMIT
  ============================================================ */
  const handleSubmit = async (): Promise<boolean> => {
    if (!submissionId) {
      toast.error("Submission ID is missing");
      return false;
    }

    if (!runValidation()) {
      return false;
    }

    if (!formData) {
      toast.error("Form data is not available");
      return false;
    }

    try {
      setLoading(true);

      const allFields: FormField[] = [
        ...(formData.fields || []),
        ...(formData.ungroupedFields || []),
        ...(formData.subSections?.flatMap((s: FormSubSection) => s.fields) || []),
      ];

      const payload: Record<string, string> = {};

      allFields.forEach((field) => {
        if (!field.isEnabled) return;
        if (["name", "email_address", "Contributor_ID"].includes(field.name))
          return;

        if (field.name === "background_other") {
          payload["backgroundOther"] = values[field.name] || "";
        } else {
          payload[field.name] = values[field.name] || "";
        }
      });

      const result = await saveContributorDetails(submissionId, payload);

      if (result.success) {
        toast.success(result.message || "Contributor details saved successfully!", {
          style: {
            backgroundColor: "#10b981",
            color: "#ffffff",
            fontSize: "16px",
            fontWeight: "500",
          },
        });
        lastSavedRef.current = values;
        setHasSavedOnce(true);
        setIsDirty(false);
        return true;
      } else {
        toast.error(result.error || "Failed to save contributor details");
        return false;
      }
    } catch (err: any) {
      console.error("Submit error:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "An unexpected error occurred while saving";
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Get all fields for rendering
  const getAllFields = (): FormField[] => {
    if (!formData) return [];
    return [
      ...(formData.fields || []),
      ...(formData.ungroupedFields || []),
      ...(formData.subSections?.flatMap((s: FormSubSection) => s.fields) || []),
    ];
  };

  // Check if background_other should be enabled
  // Check both the value and label to handle different dropdown formats
  const isBackgroundOtherEnabled = (): boolean => {
    const backgroundValue = values["background"] || "";
    if (!backgroundValue) return false;
    
    // Check if the value or label contains "other" (case-insensitive)
    const valueLower = backgroundValue.toLowerCase().trim();
    
    // Also check against the dropdown options if formData is available
    if (formData) {
      const allFields: FormField[] = [
        ...(formData.fields || []),
        ...(formData.ungroupedFields || []),
        ...(formData.subSections?.flatMap((s: FormSubSection) => s.fields) || []),
      ];
      
      const backgroundField = allFields.find((f) => f.name === "background");
      if (backgroundField && backgroundField.type === "DROPDOWN") {
        const options = backgroundField.dropdown?.options || [];
        const selectedOption = options.find(
          (opt: any) => String(opt.value).toLowerCase() === valueLower ||
          String(opt.label).toLowerCase() === valueLower
        );
        
        if (selectedOption) {
          const labelLower = String(selectedOption.label || "").toLowerCase();
          return labelLower.includes("other") || valueLower.includes("other");
        }
      }
    }
    
    return valueLower === "other" || valueLower.includes("other");
  };

  return {
    formData,
    values,
    loading,
    errors,
    handleChange,
    handleSubmit,
    getAllFields,
    isBackgroundOtherEnabled,
    hasSavedOnce,
    isDirty,
  };
};

